import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ColumnConditionValue, TableColumnListColumnValue, TableColumnListValue } from '@makyoapp/makyo-common'
import {
  ColorVariant,
  ColumnTypes,
  HoverEffectVariant,
  HoverIntensity,
  MaterialIcon,
  parseSpacing,
  PricingPlan,
  PricingTable,
  RawSpacingInput,
  Skeleton,
  SkeletonGroup,
  spacingToStyles,
} from '@makyoapp/makyo-tw-ui'

import { IDBTable } from '@/core/db/table'
import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { NumberCell } from '@/core/models/columns/number'
import { IRow } from '@/core/models/tables/row'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ComponentSettingKey } from '@/shared/builder'
import { MAX_ROWS_LIMIT } from '@/shared/constants'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { useCompPagination } from '../../hooks/useCompPagination'
import { CellComponent } from '../../shared/CellComponent'
import { getColBrief } from '../../utils'

function transformPlansData(
  rowsData: IRow[],
  columnsData: TableColumnListColumnValue[],
  device: AppBreakpoint,
  pricingSettings: any,
  dbTable: IDBTable,
  featuresRows: IRow[] = [],
  featuresTable: IDBTable | undefined
) {
  // Create mapping from column ID to label for features
  const fieldMapping: Record<string, string[]> = {}

  // Initialize arrays for each fieldId
  if (pricingSettings?.name) {
    if (!fieldMapping[pricingSettings.name]) fieldMapping[pricingSettings.name] = []
    fieldMapping[pricingSettings.name].push('name')
  }

  if (pricingSettings?.monthlyPrice) {
    if (!fieldMapping[pricingSettings.monthlyPrice]) fieldMapping[pricingSettings.monthlyPrice] = []
    fieldMapping[pricingSettings.monthlyPrice].push('monthlyPrice')
  }

  if (pricingSettings?.yearlyPrice) {
    if (!fieldMapping[pricingSettings.yearlyPrice]) fieldMapping[pricingSettings.yearlyPrice] = []
    fieldMapping[pricingSettings.yearlyPrice].push('yearlyPrice')
  }

  if (pricingSettings?.badgeText) {
    if (!fieldMapping[pricingSettings.badgeText]) fieldMapping[pricingSettings.badgeText] = []
    fieldMapping[pricingSettings.badgeText].push('badgeText')
  }

  if (pricingSettings?.buttonText) {
    if (!fieldMapping[pricingSettings.buttonText]) fieldMapping[pricingSettings.buttonText] = []
    fieldMapping[pricingSettings.buttonText].push('buttonText')
  }

  if (pricingSettings?.description) {
    if (!fieldMapping[pricingSettings.description]) fieldMapping[pricingSettings.description] = []
    fieldMapping[pricingSettings.description].push('description')
  }

  if (pricingSettings?.popular) {
    if (!fieldMapping[pricingSettings.popular]) fieldMapping[pricingSettings.popular] = []
    fieldMapping[pricingSettings.popular].push('popular')
  }

  if (pricingSettings?.yearlyDiscount) {
    if (!fieldMapping[pricingSettings.yearlyDiscount]) fieldMapping[pricingSettings.yearlyDiscount] = []
    fieldMapping[pricingSettings.yearlyDiscount].push('yearlyDiscount')
  }

  if (pricingSettings?.default) {
    if (!fieldMapping[pricingSettings.default]) fieldMapping[pricingSettings.default] = []
    fieldMapping[pricingSettings.default].push('default')
  }

  const labelToFieldId: Record<string, string> = {} // Map label -> fieldId in main table
  const fieldIdToLabel: Record<string, string> = {} // Map fieldId in main table -> label

  columnsData
    .filter((column) => column.visibility?.[device])
    .forEach((column) => {
      if (column.label) {
        let matched = false

        // Strategy 1: Try to find by column.col first (if it exists in main table)
        if (column.col && dbTable.columnsMap.has(column.col)) {
          labelToFieldId[column.label] = column.col
          fieldIdToLabel[column.col] = column.label
          matched = true
        } else {
          // Strategy 2: Find by column name (exact match first)
          for (const [fieldId, tableColumn] of dbTable.columnsMap.entries()) {
            const tableColumnName = tableColumn._Name || ''
            if (tableColumnName && tableColumnName === column.label) {
              labelToFieldId[column.label] = fieldId
              fieldIdToLabel[fieldId] = column.label
              matched = true
              break
            }
          }

          // Strategy 3: Find by column name (case-insensitive match)
          if (!matched) {
            for (const [fieldId, tableColumn] of dbTable.columnsMap.entries()) {
              const tableColumnName = tableColumn._Name || ''
              if (tableColumnName && tableColumnName.toLowerCase() === column.label.toLowerCase()) {
                labelToFieldId[column.label] = fieldId
                fieldIdToLabel[fieldId] = column.label
                matched = true
                break
              }
            }
          }
        }
      }
    })

  // Get list of fieldIds that are used for special mapping (should be excluded from features)
  const excludedFieldIds = new Set(
    [
      pricingSettings?.name,
      pricingSettings?.monthlyPrice,
      pricingSettings?.yearlyPrice,
      pricingSettings?.badgeText,
      pricingSettings?.buttonText,
      pricingSettings?.description,
      pricingSettings?.popular,
      pricingSettings?.default,
    ].filter(Boolean)
  )

  const featuresRowMap = new Map<string, IRow>()
  if (featuresTable && featuresRows.length > 0) {
    featuresRows.forEach((featureRow) => {
      const rowId = featureRow._id
      featuresRowMap.set(rowId, featureRow)
    })
  }

  // Transform each row to plan object
  const transformedPlans: PricingPlan[] = rowsData.map((row, rowIndex) => {
    const plan: {
      id: string
      name: string
      monthlyPrice: number
      yearlyPrice: number
      popular: boolean | undefined
      default: boolean | undefined
      badge: {
        text: string
        variant: 'default' | 'popular' | 'premium' | 'free' | undefined
      }
      features: Record<string, any>
      button: {
        text: string
        variant: 'default' | 'outlined' | 'destructive' | undefined
      }
    } = {
      id: row._id,
      name: '',
      monthlyPrice: 0,
      yearlyPrice: 0,
      popular: false,
      default: false,
      badge: {
        text: '',
        variant: undefined,
      },
      features: {},
      button: {
        text: '',
        variant: undefined,
      },
    }

    // Iterate over each field in the row
    Object.keys(row).forEach((fieldId) => {
      if (fieldId === '_id') return

      const fieldData = row[fieldId]
      const fieldValue = fieldData?.value
      const itemCol = getColBrief(dbTable, fieldId)

      // Use dynamic mapping based on pricingSettings instead of hardcoded fieldId
      const fieldTypes = fieldMapping[fieldId]

      if (fieldTypes) {
        for (const fieldType of fieldTypes) {
          switch (fieldType) {
            case 'name':
              const nameValue = String(fieldValue || '')
              plan.name = nameValue
              if (!plan.button.text) {
                plan.button.text = nameValue
              }
              plan.button.variant = 'default'
              break
            case 'monthlyPrice':
              plan.monthlyPrice = Number(fieldValue) || 0
              break
            case 'yearlyPrice':
              plan.yearlyPrice = Number(fieldValue) || 0
              break
            case 'popular':
              plan.popular = Boolean(fieldValue)
              break
            case 'default':
              plan.default = Boolean(fieldValue)
              break
            case 'badgeText':
              if (!plan.name) {
                plan.name = 'Free'
              }
              plan.badge.text = String(fieldValue || '')
              plan.badge.variant = undefined
              break
            case 'buttonText':
              plan.button.text = String(fieldValue || '')
              break
          }
        }
      }

      let finalFeatureLabel = fieldIdToLabel[fieldId]

      if (!finalFeatureLabel) {
        const tableColumn = dbTable.columnsMap.get(fieldId)
        const tableColumnName = tableColumn?._Name || ''

        if (tableColumnName) {
          const matchingColumn = columnsData.find((col) => {
            if (!col.label) return false
            return col.label === tableColumnName || col.label.toLowerCase() === tableColumnName.toLowerCase()
          })

          if (matchingColumn?.label) {
            finalFeatureLabel = matchingColumn.label
            fieldIdToLabel[fieldId] = finalFeatureLabel
            labelToFieldId[finalFeatureLabel] = fieldId
          }
        }
      }

      if (finalFeatureLabel && !excludedFieldIds.has(fieldId)) {
        const valueToProcess = fieldValue
        let processedValue: any = valueToProcess
        let shouldAssign = true

        if (valueToProcess === undefined || valueToProcess === null) {
          shouldAssign = false
        } else {
          if (typeof valueToProcess === 'number' && itemCol?._Settings) {
            const numberCell = new NumberCell(valueToProcess, itemCol?._Settings as any)
            processedValue = numberCell?.toString()
          } else if (typeof valueToProcess === 'string') {
            const trimmedValue = valueToProcess.trim()
            if (trimmedValue.toLowerCase() === 'true') {
              processedValue = true
            } else if (trimmedValue.toLowerCase() === 'false') {
              processedValue = false
            } else if (trimmedValue.toLowerCase() === 'unlimited') {
              processedValue = 'Unlimited'
            } else if (!isNaN(Number(trimmedValue)) && trimmedValue !== '') {
              processedValue = Number(trimmedValue)
            } else {
              processedValue = valueToProcess
            }
          } else if (typeof valueToProcess === 'boolean') {
            processedValue = valueToProcess
          } else if (valueToProcess instanceof Date) {
            processedValue = valueToProcess.toLocaleDateString()
          } else if (Array.isArray(valueToProcess)) {
            shouldAssign = false
          } else if (typeof valueToProcess === 'object' && valueToProcess !== null) {
            shouldAssign = false
          } else {
            processedValue = valueToProcess
          }
        }

        if (
          shouldAssign &&
          (typeof processedValue === 'string' ||
            typeof processedValue === 'number' ||
            typeof processedValue === 'boolean')
        ) {
          plan.features[finalFeatureLabel] = processedValue
        }
      }
    })

    if (featuresTable && featuresRows.length > 0) {
      let featureRow = featuresRows[rowIndex]

      if (!featureRow && plan.name) {
        const matchedRow = featuresRows.find((fr) => {
          return Object.values(fr).some((cell: any) => {
            const cellValue = cell?.value
            return typeof cellValue === 'string' && cellValue.toLowerCase() === plan.name.toLowerCase()
          })
        })
        if (matchedRow) {
          featureRow = matchedRow
        }
      }

      if (featureRow) {
        columnsData
          .filter((column) => column.visibility?.[device] && column.label && column.col)
          .forEach((column) => {
            if (column.col && featureRow[column.col]) {
              const cellValue = featureRow[column.col]?.value
              if (cellValue !== undefined && cellValue !== null) {
                const featureLabel = column.label
                let processedValue: any = cellValue
                let shouldAssign = true

                // Process value based on type
                const featureCol = getColBrief(featuresTable, column.col)

                if (typeof cellValue === 'number' && featureCol?._Settings) {
                  const numberCell = new NumberCell(cellValue, featureCol?._Settings as any)
                  processedValue = numberCell?.toString()
                } else if (typeof cellValue === 'string') {
                  const trimmedValue = cellValue.trim()
                  if (trimmedValue.toLowerCase() === 'true') {
                    processedValue = true
                  } else if (trimmedValue.toLowerCase() === 'false') {
                    processedValue = false
                  } else if (trimmedValue.toLowerCase() === 'unlimited') {
                    processedValue = 'Unlimited'
                  } else if (!isNaN(Number(trimmedValue)) && trimmedValue !== '') {
                    processedValue = Number(trimmedValue)
                  } else {
                    processedValue = cellValue
                  }
                } else if (typeof cellValue === 'boolean') {
                  processedValue = cellValue
                } else if (cellValue instanceof Date) {
                  processedValue = cellValue.toLocaleDateString()
                } else if (Array.isArray(cellValue)) {
                  shouldAssign = false
                } else if (typeof cellValue === 'object' && cellValue !== null) {
                  shouldAssign = false
                } else {
                  processedValue = cellValue
                }

                if (
                  shouldAssign &&
                  (typeof processedValue === 'string' ||
                    typeof processedValue === 'number' ||
                    typeof processedValue === 'boolean')
                ) {
                  plan.features[featureLabel] = processedValue
                }
              }
            }
          })
      }
    }

    columnsData
      .filter((column) => column.visibility?.[device] && column.label)
      .forEach((column) => {
        if (column.label && !(column.label in plan.features)) {
          const mappedFieldId = labelToFieldId[column.label]
          if (!mappedFieldId) {
            plan.features[column.label] = ''
          }
        }
      })
    return plan
  })

  return transformedPlans
}

function getIconForFeature(featureName: string) {
  const featureIconMap: Record<string, string> = {
    plan: 'package',
    price: 'attach_money',
    monthly: 'calendar_month',
    yearly: 'event_repeat',
    discount: 'local_offer',
    storage: 'storage',
    bandwidth: 'speed',
    users: 'people',
    support: 'support_agent',
    api: 'api',
    ssl: 'security',
    backup: 'backup',
    domain: 'language',
    email: 'email',
    database: 'database',
    analytics: 'analytics',
    templates: 'web',
    custom: 'tune',
  }

  const key = featureName.toLowerCase()

  // Search for keywords in feature name
  for (const [keyword, icon] of Object.entries(featureIconMap)) {
    if (key.includes(keyword)) {
      return icon
    }
  }

  return 'star' // default icon (star)
}

function getColorForFeature(featureName: string) {
  const featureColorMap: Record<string, string> = {
    plan: 'text-blue-500',
    price: 'text-green-500',
    monthly: 'text-purple-500',
    yearly: 'text-orange-500',
    discount: 'text-red-500',
    storage: 'text-cyan-500',
    bandwidth: 'text-indigo-500',
    users: 'text-pink-500',
    support: 'text-yellow-500',
    api: 'text-gray-500',
    ssl: 'text-emerald-500',
    backup: 'text-violet-500',
    domain: 'text-rose-500',
    email: 'text-sky-500',
    database: 'text-amber-500',
    analytics: 'text-lime-500',
    templates: 'text-teal-500',
    custom: 'text-slate-500',
  }

  const key = featureName.toLowerCase()

  // Search for keywords in feature name
  for (const [keyword, color] of Object.entries(featureColorMap)) {
    if (key.includes(keyword)) {
      return color
    }
  }

  return 'text-gray-600' // default color (gray-600)
}

function formatFeatureName(featureName: string) {
  return featureName
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
    .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

function createIconComponent(iconName: string, size = 20) {
  return <MaterialIcon icon={iconName} size={size} />
}

// Main function to generate feature categories from columns data
function generateFeaturePlans(
  columnsData: TableColumnListColumnValue[],
  device: AppBreakpoint,
  options: {
    excludeColumns?: string[]
    sortAlphabetically?: boolean
    iconSize?: number
  } = {}
) {
  const { excludeColumns = [], sortAlphabetically = true, iconSize = 20 } = options

  try {
    const featureLabels = columnsData
      .filter((column) => column.visibility?.[device])
      .filter((c) => c && c.label && c.label.length > 0)
      .filter((c) => !excludeColumns.includes(c.label))

    // Get unique labels
    const uniqueLabels = [...new Set(featureLabels.map((c) => c.label))]

    // const sortedLabels = sortAlphabetically ? uniqueLabels.sort() : uniqueLabels

    // Create a map of label to column for quick lookup
    const labelToColumn = new Map<string, TableColumnListColumnValue>()
    featureLabels.forEach((column) => {
      if (column.label && !labelToColumn.has(column.label)) {
        labelToColumn.set(column.label, column)
      }
    })

    return uniqueLabels.map((label) => {
      const column = labelToColumn.get(label) || featureLabels.find((c) => c.label === label)
      return {
        id: label,
        name: formatFeatureName(label),
        icon: createIconComponent(column?.icon || getIconForFeature(label), iconSize),
        iconColor: getColorForFeature(label),
        label: label,
        info: column?.info,
      }
    })
  } catch (error) {
    return []
  }
}

interface CompPricingWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
  featuresTableData: CoreComponentTableData
}

export function CompPricingWrapperCore({
  infos: { settings, component, currentParentBg, containerClient, detailTableId, detailRowIndex },
  action: { simulateAction },
  tableData: { dbTable: table, tableId: pricingTableId },
  featuresTableData: { dbTable: featuresTable },
}: CompPricingWrapperCoreProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  const compId = component._id

  const { device } = useBreakpoint()

  const featuresDataSource = settings?.[ComponentSettingKey.PricingDataFeaturesSource] as TableColumnListValue
  const featuresColumns = getSafely(featuresDataSource, ['columns'], [] as TableColumnListColumnValue[])

  const getBillingCycle = settings?.[ComponentSettingKey.PricingDesignBillingCycle] ?? 'monthly'

  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(getBillingCycle)
  const [currentPlan, setCurrentPlan] = useState<string>('')

  useEffect(() => {
    setBillingCycle(getBillingCycle as 'monthly' | 'yearly')
  }, [getBillingCycle, setBillingCycle])

  const pricingSettings = useMemo(
    () => ({
      featureLabel: settings?.[ComponentSettingKey.PricingDataFeatureLabel] ?? [],
      featureValue: settings?.[ComponentSettingKey.PricingDataFeatureValue] ?? [],
      name: settings?.[ComponentSettingKey.PricingDataName] ?? [],
      description: settings?.[ComponentSettingKey.PricingDataDescription] ?? [],
      monthlyPrice: settings?.[ComponentSettingKey.PricingDataMonthlyPrice] ?? [],
      yearlyPrice: settings?.[ComponentSettingKey.PricingDataYearlyPrice] ?? [],
      badgeText: settings?.[ComponentSettingKey.PricingDataBadgeText] ?? [],
      buttonText: settings?.[ComponentSettingKey.PricingDataButtonText] ?? [],
      popular: settings?.[ComponentSettingKey.PricingDataPopular] ?? [],
      yearlyDiscount: settings?.[ComponentSettingKey.PricingDataYearlyDiscount] ?? [],
      default: settings?.[ComponentSettingKey.PricingDataDefault] ?? [],
    }),
    [settings]
  )

  // dataset limit
  const datasetLimit = settings[ComponentSettingKey.CalendarDataSetLimit]
  const isLimited = datasetLimit?.enabled
  const limit = datasetLimit?.value ?? 0
  const maxPerItems = isLimited ? limit : MAX_ROWS_LIMIT

  // pagination
  const { pagination } = useCompPagination({
    compId,
    defaultPerPage: maxPerItems,
  })

  const filterSetting = settings[ComponentSettingKey.CalendarDataFilter] as ColumnConditionValue | undefined

  const sortBy = settings[ComponentSettingKey.CalendarDataSortBy] as string
  const sortType = settings[ComponentSettingKey.CalendarDataSortType] as string

  const showBadges = settings?.[ComponentSettingKey.PricingDesignShowBadges]
  const showPopular = settings?.[ComponentSettingKey.PricingDesignShowPopular]
  const showButton = settings?.[ComponentSettingKey.PricingDesignShowButton]

  const [{ rows: allRows }] = useCompTableData({
    table,
    componentId: component._id,
    filterSetting,
    sortSetting: { sortType, sortBy },
    pagination,
  })

  const [{ rows: featuresRows }] = useCompTableData({
    table: featuresTable,
    componentId: component._id,
    filterSetting: undefined,
    sortSetting: undefined,
    pagination: undefined,
    search: undefined,
    userFilter: undefined,
  })

  const planFeatures = transformPlansData(
    allRows,
    featuresColumns,
    device,
    pricingSettings,
    table,
    featuresRows,
    featuresTable
  )
  const filteredPlanFeatures = planFeatures.map((plan) => ({
    ...plan,
    badge: showBadges ? plan.badge : undefined,
    popular: showPopular ? plan.popular : false,
  }))

  const featurePlans = generateFeaturePlans(featuresColumns, device)

  const yearlyDiscount = settings?.[ComponentSettingKey.PricingDataYearlyDiscount] ?? []

  let yearlyDiscountText = ''
  const monthlyDiscountText = ''

  let firstFeatureRow: IRow | undefined

  if (currentPlan) {
    firstFeatureRow = allRows.find((row) => row._id === currentPlan)
  }

  if (!firstFeatureRow) {
    const popularFieldId = pricingSettings.popular
    if (popularFieldId) {
      firstFeatureRow = allRows.find((row) => {
        const popularCell = row[popularFieldId]
        return popularCell?.value === true
      })
    }
  }

  if (!firstFeatureRow) {
    firstFeatureRow = allRows[0]
  }

  const discountCell = firstFeatureRow?.[yearlyDiscount]
  yearlyDiscountText = (<CellComponent cell={discountCell} type={ColumnTypes.Number} />) as any

  const cardBackground = settings?.[ComponentSettingKey.PricingDesignCardBackground]
  const textPrimary = settings?.[ComponentSettingKey.PricingDesignTextPrimary]
  const textSecondary = settings?.[ComponentSettingKey.PricingDesignTextSecondary]
  const border = settings?.[ComponentSettingKey.PricingDesignBorder]
  const headerBackground = settings?.[ComponentSettingKey.PricingDesignHeaderBackground]
  const evenRowBackground = settings?.[ComponentSettingKey.PricingDesignEvenRowBackground]
  const oddRowBackground = settings?.[ComponentSettingKey.PricingDesignOddRowBackground]
  const footerBackground = settings?.[ComponentSettingKey.PricingDesignFooterBackground]
  const headerText = settings?.[ComponentSettingKey.PricingDesignHeaderText]
  const priceText = settings?.[ComponentSettingKey.PricingDesignPriceText]
  const headerBorder = settings?.[ComponentSettingKey.PricingDesignHeaderBorder]
  const accentColor = settings?.[ComponentSettingKey.PricingDesignAccentColor]
  const popularBadgeBackground = settings?.[ComponentSettingKey.PricingDesignPopularBadgeBackground]
  const popularBadgeText = settings?.[ComponentSettingKey.PricingDesignPopularBadgeText]
  const buttonPrimary = settings?.[ComponentSettingKey.PricingDesignButtonPrimary]
  const buttonPrimaryText = settings?.[ComponentSettingKey.PricingDesignButtonPrimaryText]
  const buttonSecondary = settings?.[ComponentSettingKey.PricingDesignButtonSecondary]
  const buttonSecondaryText = settings?.[ComponentSettingKey.PricingDesignButtonSecondaryText]
  const billingToggleBackground = settings?.[ComponentSettingKey.PricingDesignBillingToggleBackground]
  const billingToggleActiveBackground = settings?.[ComponentSettingKey.PricingDesignBillingToggleActiveBackground]
  const billingToggleActiveText = settings?.[ComponentSettingKey.PricingDesignBillingToggleActiveText]
  const billingToggleInactiveText = settings?.[ComponentSettingKey.PricingDesignBillingToggleInactiveText]
  const billingToggleInactiveHoverText = settings?.[ComponentSettingKey.PricingDesignBillingToggleInactiveHoverText]
  const billingToggleDiscountBackground = settings?.[ComponentSettingKey.PricingDesignBillingToggleDiscountBackground]
  const billingToggleDiscountText = settings?.[ComponentSettingKey.PricingDesignBillingToggleDiscountText]
  const columnBackgroundOpacity = settings?.[ComponentSettingKey.PricingDesignColumnBackgroundOpacity]
  const iconSuccess = settings?.[ComponentSettingKey.PricingDesignIconSuccess]
  const iconError = settings?.[ComponentSettingKey.PricingDesignIconError]
  const buttonDisabled = settings?.[ComponentSettingKey.PricingDesignButtonDisabled]
  const buttonDisabledText = settings?.[ComponentSettingKey.PricingDesignButtonDisabledText]

  // Card Settings
  const cardBorder = settings?.[ComponentSettingKey.PricingDesignCardBorder]
  const cardPopularBorder = settings?.[ComponentSettingKey.PricingDesignCardPopularBorder]
  const cardPopularRing = settings?.[ComponentSettingKey.PricingDesignCardPopularRing]
  const cardTitleText = settings?.[ComponentSettingKey.PricingDesignCardTitleText]
  const cardPriceText = settings?.[ComponentSettingKey.PricingDesignCardPriceText]
  const cardPriceSubtext = settings?.[ComponentSettingKey.PricingDesignCardPriceSubtext]
  const cardBadgeBackground = settings?.[ComponentSettingKey.PricingDesignCardBadgeBackground]
  const cardBadgeText = settings?.[ComponentSettingKey.PricingDesignCardBadgeText]
  const cardButtonPrimary = settings?.[ComponentSettingKey.PricingDesignCardButtonPrimary]
  const cardButtonPrimaryText = settings?.[ComponentSettingKey.PricingDesignCardButtonPrimaryText]
  const cardButtonSecondary = settings?.[ComponentSettingKey.PricingDesignCardButtonSecondary]
  const cardButtonSecondaryText = settings?.[ComponentSettingKey.PricingDesignCardButtonSecondaryText]
  const cardButtonDisabled = settings?.[ComponentSettingKey.PricingDesignCardButtonDisabled]
  const cardButtonDisabledText = settings?.[ComponentSettingKey.PricingDesignCardButtonDisabledText]
  const cardBackgroundType = settings?.[ComponentSettingKey.PricingDesignCardBackgroundType]
  const cardBackgroundSolid = settings?.[ComponentSettingKey.PricingDesignCardBackgroundSolid]
  const cardBackgroundGradientStart = settings?.[ComponentSettingKey.PricingDesignCardBackgroundStartGradient]
  const cardBackgroundGradientEnd = settings?.[ComponentSettingKey.PricingDesignCardBackgroundEndGradient]
  const cardBackgroundGradientDirection =
    settings?.[ComponentSettingKey.PricingDesignCardBackgroundGradientDirection] || 'to-right'

  const margin = settings?.[ComponentSettingKey.PricingDesignMargin]
  const width = settings?.[ComponentSettingKey.PricingDesignWidth] ?? ['100%']

  const layoutAlignment = settings?.[ComponentSettingKey.PricingDesignLayoutAlignment]
  const billingToggleAlignment = settings?.[ComponentSettingKey.PricingDesignBillingToggleAlignment]
  const gap = settings?.[ComponentSettingKey.PricingDesignGap]
  const layout = settings?.[ComponentSettingKey.PricingDesignLayout] ?? 'table'
  const showBillingToggle = settings?.[ComponentSettingKey.PricingDesignShowBillingToggle]

  const onClickSelectPlan = settings?.[ComponentSettingKey.PricingActionOnClickSelectPlan]

  const hoverEffect = settings?.[ComponentSettingKey.PricingDesignHoverEffect] as HoverEffectVariant
  const hoverIntensity = 'md' as HoverIntensity

  const theme = {
    background: currentParentBg as ColorVariant,
    cardBackground: cardBackground as ColorVariant,
    textPrimary: textPrimary as ColorVariant,
    textSecondary: textSecondary as ColorVariant,
    border: border as ColorVariant,
    headerBackground: headerBackground as ColorVariant,
    evenRowBackground: evenRowBackground as ColorVariant,
    oddRowBackground: oddRowBackground as ColorVariant,
    footerBackground: footerBackground as ColorVariant,
    headerText: headerText as ColorVariant,
    priceText: priceText as ColorVariant,
    headerBorder: headerBorder as ColorVariant,
    accentColor: accentColor as ColorVariant,
    popularBadgeBackground: popularBadgeBackground as ColorVariant,
    popularBadgeText: popularBadgeText as ColorVariant,
    buttonPrimary: buttonPrimary as ColorVariant,
    buttonPrimaryText: buttonPrimaryText as ColorVariant,
    buttonSecondary: buttonSecondary as ColorVariant,
    buttonSecondaryText: buttonSecondaryText as ColorVariant,
    buttonDisabled: buttonDisabled as ColorVariant,
    buttonDisabledText: buttonDisabledText as ColorVariant,
    billingToggleBackground: billingToggleBackground as ColorVariant,
    billingToggleActiveBackground: billingToggleActiveBackground as ColorVariant,
    billingToggleActiveText: billingToggleActiveText as ColorVariant,
    billingToggleInactiveText: billingToggleInactiveText as ColorVariant,
    billingToggleInactiveHoverText: billingToggleInactiveHoverText as ColorVariant,
    billingToggleDiscountBackground: billingToggleDiscountBackground as ColorVariant,
    billingToggleDiscountText: billingToggleDiscountText as ColorVariant,
    themeBillingToggleBackground: billingToggleBackground as ColorVariant,
    themeBillingToggleActiveBackground: billingToggleActiveBackground as ColorVariant,
    themeBillingToggleActiveText: billingToggleActiveText as ColorVariant,
    themeBillingToggleInactiveText: billingToggleInactiveText as ColorVariant,
    themeBillingToggleInactiveHoverText: billingToggleInactiveHoverText as ColorVariant,
    themeBillingToggleDiscountBackground: billingToggleDiscountBackground as ColorVariant,
    themeBillingToggleDiscountText: billingToggleDiscountText as ColorVariant,
    columnBackgroundOpacity: columnBackgroundOpacity,
    iconSuccess: iconSuccess as ColorVariant,
    iconError: iconError as ColorVariant,
    billingToggleAlignment: billingToggleAlignment as 'left' | 'center' | 'right',
    cardBorder: cardBorder as ColorVariant,
    cardPopularBorder: cardPopularBorder as ColorVariant,
    cardPopularRing: cardPopularRing as ColorVariant,
    cardTitleText: cardTitleText as ColorVariant,
    cardPriceText: cardPriceText as ColorVariant,
    cardPriceSubtext: cardPriceSubtext as ColorVariant,
    cardBadgeBackground: cardBadgeBackground as ColorVariant,
    cardBadgeText: cardBadgeText as ColorVariant,
    cardButtonPrimary: cardButtonPrimary as ColorVariant,
    cardButtonPrimaryText: cardButtonPrimaryText as ColorVariant,
    cardButtonSecondary: cardButtonSecondary as ColorVariant,
    cardButtonSecondaryText: cardButtonSecondaryText as ColorVariant,
    cardButtonDisabled: cardButtonDisabled as ColorVariant,
    cardButtonDisabledText: cardButtonDisabledText as ColorVariant,
    cardBackgroundType: cardBackgroundType as 'solid' | 'gradient',
    cardBackgroundSolid: cardBackgroundSolid as ColorVariant,
    cardBackgroundGradientStart: cardBackgroundGradientStart as ColorVariant,
    cardBackgroundGradientEnd: cardBackgroundGradientEnd as ColorVariant,
    cardBackgroundGradientDirection: cardBackgroundGradientDirection,
    hoverEffect: hoverEffect as HoverEffectVariant,
    hoverIntensity: hoverIntensity as HoverIntensity,
  }

  const handlePlanSelect = useCallback(
    (planId: string) => {
      try {
        if (!planId || typeof planId !== 'string') return
        setCurrentPlan(planId)
        if (onClickSelectPlan) {
          simulateAction({ actions: onClickSelectPlan, rowId: planId, tableId: table.settings._id })
        }
      } catch (error) {
        console.error('Error handling plan select:', error)
      }
    },
    [onClickSelectPlan, simulateAction, table.settings._id]
  )

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const pricingComponent = (
    <PricingTable
      plans={filteredPlanFeatures}
      featureCategories={featurePlans as any}
      billingCycle={billingCycle}
      onBillingChange={setBillingCycle}
      currentPlan={currentPlan}
      onPlanSelect={handlePlanSelect}
      showBillingToggle={showBillingToggle}
      showBadges={showBadges}
      showPopular={showPopular}
      showButton={showButton}
      yearlyDiscount={yearlyDiscountText}
      monthlyDiscount={monthlyDiscountText}
      layout={layout}
      buttonVariant='default'
      width={width}
      gap={gap}
      theme={theme}
      margin={margin}
      billingToggleAlign={billingToggleAlignment}
      alignment={layoutAlignment}
      devMode={isDevelopmentMode}
    />
  )

  return !allRows.length ? (
    <PricingSkeleton backgroundColor={currentParentBg!} margin={margin} />
  ) : isDevelopmentMode ? (
    <div className='relative inline-block w-full'>
      {pricingComponent}
      <CompOverlayDevmode handleClick={handleClick} />
    </div>
  ) : (
    <Fragment>{pricingComponent}</Fragment>
  )
}

const PricingSkeleton = ({ backgroundColor, margin }: { backgroundColor: ColorVariant; margin: RawSpacingInput }) => {
  return (
    <SkeletonGroup backgroundColor={backgroundColor}>
      <div className='w-full' style={{ ...spacingToStyles(parseSpacing(margin), 'padding') }}>
        <div className='p-8 space-y-6'>
          {[...new Array(4)].map((_, i) => (
            <div key={i} className='flex gap-4'>
              <div className='flex-1 space-y-2'>
                <Skeleton className='w-[100%] h-5 rounded-lg' />
                <Skeleton className='w-[100%] h-6 rounded-lg' />
                <Skeleton className='w-[100%] h-5 rounded-lg' />
              </div>
            </div>
          ))}
        </div>
      </div>
    </SkeletonGroup>
  )
}
